import React, { ReactNode, useContext, useState } from "react"
import { useHistory } from "react-router"
import { isHttpError } from "#/utils/HttpErrors"
import { CarrotErrorCodes, getErrorMessageFromCode } from "#/utils/CarrotErrors"
import { useIntl } from "react-intl"
import { Link } from "@carrotfertility/carotene-core"
import { useAuthentication } from "../../context/authentication/AuthenticationProvider"
import { EmployeeSupportUrl } from "../../../utils/EmployeeSupportLink"
import { carrotClient } from "#/utils/CarrotClient"

interface SamlNoEmailOnboardingProviderProps {
  onSubmit: ({ email }: { email: string }) => Promise<void>
  getTranslatedErrorMessage: (errorCode: string) => string | ReactNode | (string | ReactNode)[]
  error: string
  loading: boolean
  heading: string
  subheading: string
}

const SamlNoEmailOnboardingContext = React.createContext<SamlNoEmailOnboardingProviderProps>(null)

export const useSamlNoEmailOnboardingContext = () => useContext(SamlNoEmailOnboardingContext)

// @ts-expect-error TS(7031) FIXME: Binding element 'children' implicitly has an 'any'... Remove this comment to see the full error message
export function SamlNoEmailOnboardingProvider({ children }) {
  const [error, setError] = useState("")
  const [loading, setLoading] = useState(false)
  const history = useHistory()
  const intl = useIntl()
  const { locale } = intl
  const { handleSignOutNoRedirect } = useAuthentication()

  const heading = intl.formatMessage({ defaultMessage: "What is your email address?" })
  const subheading = intl.formatMessage({
    defaultMessage: "This is where Carrot will send you updates about your benefit, reminders, tips, and more."
  })

  // @ts-expect-error TS(7031) FIXME: Binding element 'email' implicitly has an 'any' ty... Remove this comment to see the full error message
  const onSubmit = async ({ email }) => {
    setError("")
    setLoading(true)

    try {
      const response = await carrotClient.lookup(email)

      if (response.status != null) {
        setError(response.status)
        setLoading(false)
        return
      }
      if (response?.employee != null) {
        setError(CarrotErrorCodes.EMPLOYEE_ALREADY_HAS_EMAIL)
        setLoading(false)
        return
      }
    } catch (error) {
      setError(CarrotErrorCodes.INTERNAL_SERVER_ERROR)
      setLoading(false)
      return
    }

    try {
      await carrotClient.samlNoEmailOnboardingAddEmailToAccount(email)
    } catch (error) {
      if (isHttpError(error)) {
        const { code } = await error.response.json()
        if (code === CarrotErrorCodes.EMPLOYEE_ALREADY_HAS_EMAIL || code === CarrotErrorCodes.EMPLOYEE_NOT_FOUND) {
          setError(code)
          setLoading(false)
          return
        }
      }
      setError(CarrotErrorCodes.TRY_AGAIN_LATER)
      setLoading(false)
      return
    }

    try {
      // This is an unfortunate workaround for our weird state of being authenticated by SAML (and having a session cookie),
      // but not actually being registered yet. This causes 403's from our api endpoints, which wreaks havok on our FE
      // logic.
      handleSignOutNoRedirect()

      await carrotClient.signUp(email, locale)

      history.push(`/signup-success?email=${encodeURIComponent(email)}`)
    } catch (error) {
      setError(CarrotErrorCodes.TRY_AGAIN_LATER)
      setLoading(false)
      return
    }
  }

  const getTranslatedErrorMessage = (errorCode: string): string | ReactNode | (string | ReactNode)[] => {
    if (errorCode === CarrotErrorCodes.EMPLOYEE_ALREADY_HAS_EMAIL) {
      return intl.formatMessage(getErrorMessageFromCode(errorCode), {
        link: (string) => (
          <Link color="inherit" href={EmployeeSupportUrl} target={"_blank"}>
            {string}
          </Link>
        )
      })
    }
    return intl.formatMessage(getErrorMessageFromCode(errorCode))
  }

  return (
    <SamlNoEmailOnboardingContext.Provider
      value={{ error, loading, onSubmit, heading, subheading, getTranslatedErrorMessage }}
    >
      {children}
    </SamlNoEmailOnboardingContext.Provider>
  )
}
